import { useState, useMemo } from "react";
import { TN_PATH_D, TN_VIEWBOX, TN_BOUNDS, TN_GEO } from "./_tnPathData";

const DISTRICTS = [
  { name: "Coimbatore", lat: 11.02, lng: 76.96, hq: true },
  { name: "Chennai", lat: 13.08, lng: 80.27 },
  { name: "Chengalpattu", lat: 12.69, lng: 79.98 },
  { name: "Kanchipuram", lat: 12.83, lng: 79.70 },
  { name: "Tiruvallur", lat: 13.14, lng: 79.91 },
  { name: "Vellore", lat: 12.92, lng: 79.13 },
  { name: "Ranipet", lat: 12.93, lng: 79.32 },
  { name: "Tirupathur", lat: 12.50, lng: 78.57 },
  { name: "Krishnagiri", lat: 12.53, lng: 78.21 },
  { name: "Dharmapuri", lat: 12.13, lng: 78.16 },
  { name: "Salem", lat: 11.66, lng: 78.15 },
  { name: "Namakkal", lat: 11.22, lng: 78.17 },
  { name: "Erode", lat: 11.34, lng: 77.72 },
  { name: "Tiruppur", lat: 11.10, lng: 77.34 },
  { name: "Nilgiris", lat: 11.41, lng: 76.69 },
  { name: "Karur", lat: 10.96, lng: 78.08 },
  { name: "Tiruchirappalli", lat: 10.79, lng: 78.70 },
  { name: "Perambalur", lat: 11.23, lng: 78.88 },
  { name: "Ariyalur", lat: 11.14, lng: 79.08 },
  { name: "Cuddalore", lat: 11.74, lng: 79.77 },
  { name: "Villupuram", lat: 11.94, lng: 79.50 },
  { name: "Kallakurichi", lat: 11.74, lng: 78.96 },
  { name: "Tiruvannamalai", lat: 12.23, lng: 79.07 },
  { name: "Thanjavur", lat: 10.79, lng: 79.14 },
  { name: "Tiruvarur", lat: 10.77, lng: 79.63 },
  { name: "Nagapattinam", lat: 10.76, lng: 79.84 },
  { name: "Mayiladuthurai", lat: 11.10, lng: 79.65 },
  { name: "Pudukkottai", lat: 10.38, lng: 78.82 },
  { name: "Sivaganga", lat: 9.86, lng: 78.48 },
  { name: "Ramanathapuram", lat: 9.37, lng: 78.83 },
  { name: "Madurai", lat: 9.93, lng: 78.12 },
  { name: "Theni", lat: 10.00, lng: 77.48 },
  { name: "Dindigul", lat: 10.36, lng: 77.97 },
  { name: "Virudhunagar", lat: 9.59, lng: 77.96 },
  { name: "Tenkasi", lat: 8.96, lng: 77.31 },
  { name: "Tirunelveli", lat: 8.73, lng: 77.71 },
  { name: "Thoothukudi", lat: 8.78, lng: 78.13 },
  { name: "Kanyakumari", lat: 8.08, lng: 77.55 },
];

const project = (lat, lng) => {
  const x =
    TN_BOUNDS.x0 +
    ((lng - TN_GEO.lngWest) / (TN_GEO.lngEast - TN_GEO.lngWest)) *
      (TN_BOUNDS.x1 - TN_BOUNDS.x0);
  const y =
    TN_BOUNDS.y0 +
    ((TN_GEO.latNorth - lat) / (TN_GEO.latNorth - TN_GEO.latSouth)) *
      (TN_BOUNDS.y1 - TN_BOUNDS.y0);
  return { x, y };
};

// Build district pins as a static SVG markup string. This avoids React array
// reconciliation interactions with visual-edit instrumentation that prevent
// SVG circle elements from rendering with non-zero bounding boxes.
const buildPinsMarkup = (hovered) => {
  return DISTRICTS.filter((d) => !d.hq)
    .map((d) => {
      const { x, y } = project(d.lat, d.lng);
      const r = hovered === d.name ? 32 : 22;
      return `<circle data-district="${d.name}" cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="${r}" fill="#D4A95A" stroke="#0B1F3A" stroke-width="3" style="cursor:pointer;transition:r 200ms ease" />`;
    })
    .join("");
};

const TamilNaduMap = () => {
  const [hovered, setHovered] = useState(null);
  const hq = DISTRICTS.find((d) => d.hq);
  const hqProj = project(hq.lat, hq.lng);
  const pinsMarkup = useMemo(() => buildPinsMarkup(hovered), [hovered]);

  const onMouseMove = (e) => {
    const t = e.target;
    if (t && t.tagName === "circle" && t.getAttribute("data-district")) {
      const name = t.getAttribute("data-district");
      if (name !== hovered) setHovered(name);
    } else if (hovered) {
      setHovered(null);
    }
  };

  const hoverLabel = (() => {
    if (!hovered) return null;
    const d = DISTRICTS.find((x) => x.name === hovered);
    if (!d) return null;
    const { x, y } = project(d.lat, d.lng);
    const w = d.name.length * 18 + 32;
    return { name: d.name, x, y, w };
  })();

  return (
    <div className="relative w-full" data-testid="tamilnadu-map">
      <svg
        viewBox={TN_VIEWBOX}
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Tamil Nadu map showing all 38 districts served by Wintrust Solutions, with Coimbatore as the headquarters"
        onMouseMove={onMouseMove}
        onMouseLeave={() => setHovered(null)}
      >
        <defs>
          <filter id="tn-shadow" x="-5%" y="-5%" width="110%" height="110%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
            <feOffset dx="0" dy="8" result="offsetblur" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.18" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="pin-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D4A95A" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#D4A95A" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Tamil Nadu state shape */}
        <path
          d={TN_PATH_D}
          fill="#0B1F3A"
          stroke="#D4A95A"
          strokeWidth="3"
          filter="url(#tn-shadow)"
          opacity="0.95"
        />

        {/* HQ pulsing glow */}
        <circle
          cx={hqProj.x}
          cy={hqProj.y}
          r="120"
          fill="url(#pin-glow)"
          className="tn-pulse"
        />

        {/* District pins - injected as raw SVG to bypass visual-edit instrumentation */}
        <g dangerouslySetInnerHTML={{ __html: pinsMarkup }} />

        {/* Hover label */}
        {hoverLabel && (
          <g pointerEvents="none">
            <rect
              x={hoverLabel.x + 32}
              y={hoverLabel.y - 36}
              width={hoverLabel.w}
              height="56"
              rx="10"
              fill="#0B1F3A"
              stroke="#D4A95A"
              strokeWidth="2"
            />
            <text
              x={hoverLabel.x + 48}
              y={hoverLabel.y + 1}
              fill="#FFFFFF"
              fontSize="32"
              fontWeight="700"
              fontFamily="Inter, sans-serif"
            >
              {hoverLabel.name}
            </text>
          </g>
        )}

        {/* HQ pin: Coimbatore (always visible, larger, with permanent label) */}
        <circle
          cx={hqProj.x}
          cy={hqProj.y}
          r="36"
          fill="#D4A95A"
          stroke="#FFFFFF"
          strokeWidth="6"
        />
        <circle cx={hqProj.x} cy={hqProj.y} r="14" fill="#0B1F3A" />
        <rect
          x={hqProj.x + 44}
          y={hqProj.y - 32}
          width="320"
          height="64"
          rx="10"
          fill="#D4A95A"
        />
        <text
          x={hqProj.x + 66}
          y={hqProj.y + 10}
          fill="#0B1F3A"
          fontSize="34"
          fontWeight="800"
          fontFamily="Inter, sans-serif"
        >
          Coimbatore HQ
        </text>
      </svg>

      <style>{`
        .tn-pulse {
          transform-origin: center;
          transform-box: fill-box;
          animation: tnPulse 2.4s ease-in-out infinite;
        }
        @keyframes tnPulse {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.6); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default TamilNaduMap;
